import './polyfills'; // Import polyfills before anything else

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import FlagProvider, { IConfig } from '@unleash/proxy-client-react';
import App from 'App';
import dayjs from 'dayjs';
import advanced from 'dayjs/plugin/advancedFormat';
import calendar from 'dayjs/plugin/calendar';
import isToday from 'dayjs/plugin/isToday';
import isTomorrow from 'dayjs/plugin/isTomorrow';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { PersistGate } from 'redux-persist/integration/react';

import { persistor, store } from 'store';

import ErrorPage from 'pages/Error';
import { captureVerifyLinkToken } from 'pages/VerifyIdentity/utils/verifyLinkToken';

import Loader from 'shared/Loader';

import { AuthBootstrapProvider } from 'contexts/authBootstrapContext';
import { MessagesProvider } from 'contexts/messagesContext/messagesContext';
import socketMessages from 'socket/socketMessages';
import { appendScript } from 'utils/appendScript';
import { IS_LIVE } from 'utils/constants';
import {
  finishLoginCallback,
  installLoginService,
  LOGIN_CALLBACK_PATH,
  loginServiceConfig,
  loginServiceOrigin,
  showLoginCallbackError
} from 'utils/loginService';
import { handleCallback } from '@thecvlb/auth-web/callback';
import { connectLoginService, waitForRehydration } from 'utils/loginServiceBridge';
import { applyLoginChoiceFromUrl } from 'utils/loginServiceOptIn';

import 'config/i18n';
import 'chart.js/auto';
import 'utils/chartjsAdapterDayjs';

import { CommonSocketProvider } from './contexts/commonSocketContext/commonSocketContext';
import { initDatadog, logDataDogEvent } from './utils/datadog';
import { initTagManager } from './utils/gtm';

import 'index.css';
import 'react-sliding-pane/dist/react-sliding-pane.css';
import 'react-medium-image-zoom/dist/styles.css';
import 'react-day-picker/style.css';
import 'react-horizontal-scrolling-menu/dist/styles.css';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import 'react-loading-skeleton/dist/skeleton.css';
import socketFrontDesk from './socket/socketFrontDesk';
import { FrontDeskProvider } from './contexts/frontDeskContext/frontDeskContext';

/**
 * Normalizes unknown errors into Error instances for consistent logging.
 */
const normalizeError = (error: unknown): Error =>
  error instanceof Error ? error : new Error('Unknown error');

dayjs.extend(utc);
dayjs.extend(calendar);
dayjs.extend(timezone);
dayjs.extend(advanced);
dayjs.extend(isToday);
dayjs.extend(isTomorrow);
dayjs.extend(relativeTime);

// Before any telemetry starts, and deliberately so. The click-to-verify link's token rides in the
// URL fragment; the Datadog call below snapshots `location.href` into the first RUM view, GTM's
// page tags read it on the initial pageview, and PostHog — initialised from App's mount effect —
// stores it as the session's entry URL for every event that follows. The page that consumes the
// token is lazily routed and mounts far too late to prevent any of that.
//
// Earliest in this module, which is not earliest on the page: this bundle is `type="module"` and
// therefore deferred until the document has been parsed, so `index.html`'s own vendor scripts run
// first. The inline script at the top of `index.html` is what actually clears the fragment; by
// the time this line runs it is normally reading that script's handoff rather than the hash.
captureVerifyLinkToken();

/**
 * The login service's callback (LMD-682): `handleCallback` takes `#code=&state=` out of the URL and
 * hands it to the tab that started sign-in. Handled before telemetry, like the verify link above,
 * and nothing else is installed on this page.
 */
const isLoginCallback = !!loginServiceConfig() && window.location.pathname === LOGIN_CALLBACK_PATH;

if (isLoginCallback) {
  finishLoginCallback(handleCallback, {
    leave: () => window.location.replace('/'),
    showError: () => showLoginCallbackError(document.getElementById('root'))
  });
} else {
  initDatadog();
  initTagManager();

  IS_LIVE && appendScript(import.meta.env.BASE_URL + './js/equalWeb.js');
}

const config: IConfig = {
  appName: 'LifeMD Patient Portal',
  clientKey: import.meta.env.VITE_UNLEASH_CLIENT_KEY || '',
  environment: import.meta.env.VITE_UNLEASH_ENV,
  refreshInterval: 60,
  url: import.meta.env.VITE_UNLEASH_PROXY_URL || ''
};
const container = document.getElementById('root');
const root = createRoot(container as HTMLElement);

const renderApp = () => {
  root.render(
    <StrictMode>
      <FlagProvider config={config}>
        <Provider store={store}>
          <PersistGate loading={<Loader isVisible />} persistor={persistor}>
            <AuthBootstrapProvider>
              <MessagesProvider socket={socketMessages}>
                <FrontDeskProvider socket={socketFrontDesk}>
                  <CommonSocketProvider socket={socketMessages}>
                    <BrowserRouter>
                      <ErrorBoundary
                        FallbackComponent={ErrorPage}
                        onError={(error, context) => {
                          const normalizedError = normalizeError(error);
                          logDataDogEvent({
                            context,
                            error: normalizedError,
                            level: 'error',
                            message:
                              normalizedError.message ||
                              context.componentStack ||
                              'Runtime error in React component'
                          });
                        }}
                      >
                        <App />
                      </ErrorBoundary>
                    </BrowserRouter>
                  </CommonSocketProvider>
                </FrontDeskProvider>
              </MessagesProvider>
            </AuthBootstrapProvider>
          </PersistGate>
        </Provider>
      </FlagProvider>
    </StrictMode>
  );
};

void (async () => {
  if (isLoginCallback) {
    return;
  }

  // A failure here must never leave a blank page: log it and render anyway.
  try {
    const auth = installLoginService();

    if (auth) {
      await waitForRehydration(persistor);
      await connectLoginService(auth);
    }

    // `?login=` opts this browser in or out of the login service (utils/loginServiceOptIn);
    // a change reloads the page so the SDK starts with it.
    if (await applyLoginChoiceFromUrl(auth, !!loginServiceOrigin())) {
      return;
    }
  } catch (error) {
    const normalizedError = normalizeError(error);
    logDataDogEvent({
      context: { componentStack: 'loginService' },
      error: normalizedError,
      level: 'error',
      message: normalizedError.message
    });
  }

  if (import.meta.env.DEV && import.meta.env.VITE_POMIF_FLAG_ONLY === 'true') {
    const { startPomifFlagOnly } = await import('mocks/browser');
    await startPomifFlagOnly();
  } else if (import.meta.env.DEV && import.meta.env.VITE_POMIF_MSW === 'true') {
    const { startPomifMsw } = await import('mocks/browser');
    await startPomifMsw();
  } else if (import.meta.env.DEV && import.meta.env.VITE_INSURANCE_WIZARD_USE_MSW === 'true') {
    const { startInsuranceWizardMsw } = await import('mocks/browser');
    await startInsuranceWizardMsw();
  }
  renderApp();
})();
